// app_client/src/hooks/useInvoiceCalculator.js
import { useCallback } from 'react';
import { calculateInvoiceRow as calculateRow } from '../utils/invoiceMath';
import useInvoiceStore from '../store/useInvoiceStore';

const useInvoiceCalculator = (binding = null) => {
  const store = useInvoiceStore();
  const { activeInvoice, setActiveInvoiceItems, setTotals } = binding || store;

  const round = (num) => Math.round((num + Number.EPSILON) * 100) / 100;

  // 1. GENEL TOPLAMLARI HESAPLA
  const calculateAllTotals = useCallback((items, exchangeRate = activeInvoice?.exchangeRate) => {
    const rate = parseFloat(exchangeRate) || 1;
  
    // Rakamları zorla Number tipine dönüştürerek topla
    const raw = items.reduce((acc, item) => {
      acc.subTotal += Number(item.totalAmount || 0);
      acc.totalVat += Number(item.vatAmount || 0);
      acc.grandTotal += Number(item.grandTotal || 0);
      return acc;
    }, { subTotal: 0, totalVat: 0, grandTotal: 0 });

    // Store'a gönderilecek veriyi hazırla[cite: 1]
    const totals = {
      subTotal: round(raw.subTotal),
      totalVat: round(raw.totalVat),
      grandTotal: round(raw.grandTotal),
      exchangeRate: rate
    };

    // ÖNEMLİ: updateActiveInvoiceField üzerinden tek tek veya 
    // setTotals fonksiyonunun activeInvoice'u güncellediğinden emin ol[cite: 1, 4]
    setTotals(totals); 
  }, [activeInvoice?.exchangeRate, setTotals]);

  // 3. INPUT DEĞİŞİMİNDE TETİKLENEN FONKSİYON
  const handleItemChange = useCallback((index, field, value) => {
    if (!activeInvoice?.items) return;
    const newItems = [...activeInvoice.items];
    
    const numericFields = ['quantity', 'unitPrice', 'vatRate', 'discountValue'];
    const finalValue = numericFields.includes(field) ? (parseFloat(value) || 0) : value;

    let updatedItem = { ...newItems[index], [field]: finalValue };

    if ([...numericFields, 'isVatIncluded', 'discountType'].includes(field)) {
      updatedItem = calculateRow(updatedItem);
    }

    newItems[index] = updatedItem;
    setActiveInvoiceItems(newItems);
    calculateAllTotals(newItems);
  }, [activeInvoice?.items, setActiveInvoiceItems, calculateAllTotals]);

  // 4. YENİ ÜRÜN FORMATLAMA
  const formatNewProduct = useCallback((prod) => {
    const isPurchase = activeInvoice?.type?.includes('Purchase');
    const basePriceTL = isPurchase ? prod.purchasePrice : prod.salePrice;
    const vatRate = isPurchase ? (prod.purchaseVatRate ?? 20) : (prod.saleVatRate ?? 20);
    const isVatIncluded = isPurchase ? prod.purchaseVatIncluded : prod.saleVatIncluded;
    const currentRate = activeInvoice?.exchangeRate || 1;

    const unitPriceTL = basePriceTL;
    const unitPrice = activeInvoice?.currency !== 'TRY' ? round(unitPriceTL / currentRate) : unitPriceTL;

    return calculateRow({
      productId: prod._id,
      name: prod.name,
      quantity: 1,
      unitPrice,
      originalTLPrice: unitPriceTL,
      vatRate,
      isVatIncluded,
      description: prod.description || '',
      discountValue: 0,
      discountType: 'percentage'
    });
  }, [activeInvoice?.type, activeInvoice?.currency, activeInvoice?.exchangeRate]);

  const recalculateItemsWithRate = (items, newRate) => {
    return items.map(item => {
      // Ürünün orijinal TL fiyatını yeni kura bölerek yeni birim fiyatı buluyoruz
      const newUnitPrice = Number(item.originalTLPrice || item.unitPrice) / Number(newRate);
    
      // formatNewProduct fonksiyonunuzun mantığını burada her satır için tekrar çalıştırın
      // (Miktar, KDV ve İskonto koruyarak yeni birim fiyatla hesaplama yapın)
      return formatNewProduct({
        ...item,
        unitPrice: newUnitPrice
      });
    });
  };

  return { handleItemChange, formatNewProduct, calculateAllTotals, recalculateItemsWithRate, calculateRow };
};

export default useInvoiceCalculator;
